import posthog from 'posthog-js'
import { classifyAnalyticsUrl } from '@/lib/analytics-url-policy'
import { scheduleWhenIdle } from '@/lib/schedule-idle'

/**
 * Único dueño del session replay de PostHog en la web: lo arranca en idle y lo
 * para o reanuda según la ruta, en cada `$pageview`.
 *
 * Por qué hay ruta bloqueada: la entrega del vídeo (`capability` en
 * `lib/analytics-url-policy`, ADR-055) se mide en PostHog, pero el grabador
 * manda la URL en sus metadatos sin pasar por `before_send` —el token viajaría
 * tal cual— y el DOM lleva el nombre del niño y las URLs firmadas del vídeo.
 *
 * Lo que garantiza y lo que no:
 * - **Carga completa** en la entrega (correo, vuelta de Stripe, enlace
 *   compartido: todas las formas de llegar hoy): el grabador no llega a
 *   arrancar, porque la primera sincronización ya ve la ruta bloqueada.
 * - **Navegación de cliente** hacia la entrega (hoy no hay ningún enlace
 *   interno que la haga): el `$pageview` llega DESPUÉS del `pushState`, así
 *   que el grabador puede ver el arranque de la página antes de pararse. Es un
 *   límite aceptado en ADR-055, no un descuido; si aparece un enlace interno a
 *   la entrega, el sitio para cerrarlo es la config de replay del proyecto
 *   (lista de URLs bloqueadas, aplicada por el propio grabador).
 *
 * El estado no se copia: se pregunta al SDK (`sessionRecordingStarted`), así
 * que un `reset` o un opt-out no dejan un espejo desfasado.
 */
function syncWithRoute(): void {
  const shouldRecord = classifyAnalyticsUrl() !== 'capability'
  try {
    if (shouldRecord === posthog.sessionRecordingStarted()) return
    if (shouldRecord) posthog.startSessionRecording()
    else posthog.stopSessionRecording()
  } catch (error) {
    console.error('[PostHog] Failed to sync session recording with the route:', error)
  }
}

let enabled = false

/**
 * Activa el replay guardado por ruta. Idempotente. Arranca en idle, no en
 * `load`: cargar `posthog-recorder.js` costaba ~152 ms de frame bloqueante, el
 * mayor contribuyente medido al INP de campo.
 *
 * NUNCA en la app nativa: App Store Guideline 5.1.2(i) (ATT). Lo decide quien
 * llama (`instrumentation-client`).
 */
export function enableRouteGuardedSessionRecording(): void {
  if (enabled) return
  enabled = true
  posthog.on('eventCaptured', (event: { event?: string } | undefined) => {
    if (event?.event === '$pageview') syncWithRoute()
  })
  scheduleWhenIdle(syncWithRoute)
}
